import { ZonedDateTimeFactory, ZonedDateTimeFormatter } from '@csp/csp-common-date-util';
import { PagedResultMapper } from '@csp/csp-common-mapper';
import { ApiOptions, Maybe, OrgId, PagedResult } from '@csp/csp-common-model';
import { PagedResultUtil } from '@csp/csp-common-util';
import { toRestOptions } from '@csp/csp-fe-auth';
import { ArchivingRestServiceV1, BinaryFileData } from '@csp/dmdp-api-client';
import { Page } from '@csp/dmdp-api-common-dto';
import {
  ArchiveFileCriteriaFieldsV1,
  ArchiveFileQueryTypeV1,
  ArchiveFilesV1,
} from '@csp/dmdp-api-file-storage-archiving-dto';
import { ArchiveFilesClassificationAggregationMapper } from '../mapper/ArchiveFilesClassificationAggregationMapper';
import { ArchiveFilesQueryMapper } from '../mapper/ArchiveFilesQueryMapper';
import { ArchivingFile } from '../model/ArchivingFile';
import { ArchivingFilesQueryFilter } from '../model/ArchivingFilesQueryFilter';
import { FolderPath } from '../model/FolderPath';
import { FolderTree } from '../model/FolderTree';

const getAllFilesByCorrelationId = async (
  correlationId: string,
  apiOptions?: ApiOptions,
): Promise<Pick<ArchivingFile, 'archiveFileId' | 'fileName'>[]> => {
  const getPage = async (page: Page): Promise<ArchiveFilesV1> =>
    ArchivingRestServiceV1.queryArchiveFiles(
      toRestOptions(apiOptions),
      {
        criterion: {
          fieldCriterion: {
            field: ArchiveFileCriteriaFieldsV1.CORRELATION_ID,
            type: ArchiveFileQueryTypeV1.EQ,
            values: [correlationId],
          },
        },
      },
      page,
    );

  return PagedResultUtil.getAllItems(getPage, 'archiveFiles', ({ archiveFileId, fileName }) => ({
    archiveFileId,
    fileName,
  }));
};

const getArchivedFiles = async (
  filter: ArchivingFilesQueryFilter,
  folderPath: FolderPath,
  next?: string,
  apiOptions?: ApiOptions,
): Promise<PagedResult<ArchivingFile>> => {
  const query = ArchiveFilesQueryMapper.byFilter(filter, [
    {
      fieldCriterion: {
        field: ArchiveFileCriteriaFieldsV1.CLASSIFICATION,
        type: ArchiveFileQueryTypeV1.EQ,
        values: [folderPath],
      },
    },
  ]);

  const { archiveFiles: archiveFilesV1, paging } = await ArchivingRestServiceV1.queryArchiveFiles(
    toRestOptions(apiOptions),
    query,
    { limit: filter.pageSize, count: !!filter.count, next },
  );

  const toFormattedDateString = (dateStr: string): string => {
    const zonedDateTime = ZonedDateTimeFactory.fromZonedDateTimeString(dateStr);
    return ZonedDateTimeFormatter.toDisplayDateTimeString(zonedDateTime, zonedDateTime.zone);
  };

  const archiveFiles: ArchivingFile[] = archiveFilesV1.map(
    ({ archiveFileId, fileName, mimeType, classification, orgId, correlationId, eventId, audit, fileSize }) => ({
      archiveFileId,
      fileName,
      mimeType,
      classification,
      orgId,
      correlationId,
      eventId,
      createdAt: toFormattedDateString(audit.createdAt),
      createdBy: audit.createdBy,
      fileSize,
    }),
  );

  return PagedResultMapper.toPagedResult(paging || {}, archiveFiles);
};

const getFolderTree = async (folderPath: FolderPath, apiOptions?: ApiOptions): Promise<FolderTree[]> => {
  const aggregatedClassifications = await ArchivingRestServiceV1.getAggregatedClassifications(
    toRestOptions(apiOptions),
    { classification: folderPath },
  );

  return ArchiveFilesClassificationAggregationMapper.toFolderTree(aggregatedClassifications);
};

const getBinaryFileData = async (archiveFileId: string, apiOptions?: ApiOptions): Promise<BinaryFileData> =>
  ArchivingRestServiceV1.getBinaryFileData(toRestOptions(apiOptions), archiveFileId);

const createSiteFilesBundle = async (orgId: OrgId, apiOptions?: ApiOptions): Promise<Maybe<string>> =>
  ArchivingRestServiceV1.createSiteFilesBundle(toRestOptions(apiOptions), { orgId });

const downloadSiteFilesBundle = async (signedUrl: string, apiOptions?: ApiOptions): Promise<Maybe<BinaryFileData>> => {
  const response = await ArchivingRestServiceV1.downloadSiteFilesBundle(toRestOptions(apiOptions), signedUrl);

  // Bundle is not yet available for download.
  if (response.status === 204) {
    return undefined;
  }

  return {
    blob: response.data,
    contentType: (response.headers['content-type'] ?? 'application/zip') as string,
  };
};

export const ArchivingService = {
  getArchivedFiles,
  getFolderTree,
  getBinaryFileData,
  createSiteFilesBundle,
  downloadSiteFilesBundle,
  getAllFilesByCorrelationId,
};
